import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DEFAULT_INVENTORY_SOURCE,
  InventorySourceSchema,
} from "../../fulfillments/inventory-source";
import { locationIdFormat, transferSessionIdFormat } from "../../ids";

export enum PickSessionStatus {
  CREATING = "creating",
  ACTIVE = "active",
  PICKED = "picked",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  PRINTED = "printed",
  PROBLEM = "problem",
}

export enum ScanType {
  SCAN_AT_CART = "scan_at_cart",
  SCAN_AT_WALL = "scan_at_wall",
}

export enum PickSessionStrategy {
  SINGLE_ITEM = "single_item",
  MULTI_ITEM = "multi_item",
  // BULK_SHIPMENT = "bulk_shipment",
}

export const defaultStrategyOrderCount: Record<PickSessionStrategy, number> = {
  [PickSessionStrategy.SINGLE_ITEM]: 30,
  [PickSessionStrategy.MULTI_ITEM]: 8,
};

export const PickSessionItemSchema = z.object({
  _id: zExt.objectId(),
  fulfillmentGroupId: z.string(),
  orderName: z.string().nullish(),
  imageUrl: z.string().nullish(),
  sku: z.string().nullish(),
  customAttributes: z.array(z.object({ key: z.string(), value: z.string() })),
  totalQuantity: z.number(),
  quantityPicked: z.number(),
  quantityOutOfStock: z.number(),
  quantityByTote: z
    .record(z.string(), z.object({ quantity: z.number() }))
    .nullish(),
  rawBin: z.string().nullish(),
  bin: z.string().nullish(),
  secondaryBins: z.array(z.string()).nullish(),
  hasCoordinate: z.boolean().nullish(),
  productTitle: z.string(),
  variantTitle: z.string(),
  variantId: z.string().nullish(),
  barcode: z.string().nullish(),
  externalId: z.string().nullish(),
  productCustomString1: z.string().nullish(),
  productCustomString2: z.string().nullish(),
  productCustomString3: z.string().nullish(),
  variantCustomString1: z.string().nullish(),
  variantCustomString2: z.string().nullish(),
  variantCustomString3: z.string().nullish(),
  vendor: z.string().nullish(),
  skipIndex: z.number().nullish(),
  storageLocationId: z.string().nullish(),
  productId: z.string().nullish(),
  // True when this item is on the pick despite lacking enough pickable
  // inventory to fulfill it, because it is allowed by the team's
  // allowed-problem-pick policy. The item may have no bin at all, a bin with
  // too little/no stock, or stock that is reserved for problem orders — so
  // storageLocationId may or may not be set.
  allowedProblemPickByPolicy: z.boolean().nullish(),
  lotNumber: z.string().nullish(),
  inventoryLevelId: z.string().nullish(),
  inventoryItemId: z.string().nullish(),
  orderNote: z.string().nullish(),
  // User who picked this specific item. Set when the session is zone-routed
  // and an item is picked, so attribution survives a hand-off to a later
  // zone's continuation session. Null on items in non-zone-routed sessions
  // and on unpicked items.
  pickingUserId: zExt.objectId().nullish(),
  // Timestamp this specific item was marked picked. Pairs with
  // `pickingUserId` for per-item attribution in zone-routed sessions and
  // remains null otherwise.
  pickedAt: z.date().nullish(),
  // Zone the item's bin resolves to. Set when the wave is generated under
  // sequential zone picking; used to (a) filter the picker's view to only
  // items in the session's zone, (b) gate session-advance/complete checks
  // to in-scope items, and (c) route deferred items to the right
  // continuation session at split time. Null on items in non-zone-routed
  // sessions — pairs with a null `PickSession.zoneId` to leave existing
  // behavior unchanged.
  zoneId: z.string().nullish(),
});
export type PickSessionItem = z.infer<typeof PickSessionItemSchema>;
export interface IPickSessionItemSchema extends z.ZodType<PickSessionItem> {}

export const PickSessionStatsSchema = z.object({
  averageDistanceFromSeed: z.number(),
});
export type PickSessionStats = z.infer<typeof PickSessionStatsSchema>;
export interface IPickSessionStatsSchema extends z.ZodType<PickSessionStats> {}

export const PickSessionBaseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  crdbLocationId: locationIdFormat.schema.nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  user: zExt.objectId().nullish(),
  // Set when the session was explicitly queued for the assignee by another
  // action (e.g. bulk-create-pick-session-from-FG-table). Null for sessions
  // a user creates for themselves via the WMS pick-session create flow.
  // Used to render "New assignment" vs "Resume" buckets on the pick-sessions
  // landing page: a session with `assigningUser != null` and zero items
  // picked is treated as a new assignment.
  assigningUser: zExt.objectId().nullish(),
  status: createEnum(PickSessionStatus, { enumName: "PickSessionStatus" }),
  scanType: createEnum(ScanType, { enumName: "ScanType" }),
  items: z.array(PickSessionItemSchema),
  stats: PickSessionStatsSchema,
  strategy: createEnum(PickSessionStrategy, {
    enumName: "PickSessionStrategy",
  }),
  inventorySource: InventorySourceSchema.default(DEFAULT_INVENTORY_SOURCE),
  completedAt: z.date().nullish(),
  pickedAt: z.date().nullish(),
  printedAt: z.date().nullish(),
  sourcingCompletedAt: z.date().nullish(),
  filterName: z.string().nullish(),
  fullTotes: z.array(z.string()).nullish(),
  markedProblemBy: zExt.objectId().nullish(),
  transferSessionId: transferSessionIdFormat.schema.nullish(),
  // Zone the session is rooted in for sequential zone picking. The picker
  // assigned to this session works `zoneId` exclusively; FGs whose first
  // zone is `zoneId` enter here, and continuation sessions for downstream
  // zones are spawned at session completion (PR 2 split flow). Null on all
  // sessions for teams with `zonePickingMode = NONE` — the existing flow
  // is unchanged.
  zoneId: z.string().nullish(),
});

const SingleItemPickSessionSchema = PickSessionBaseSchema.extend({
  strategy: z.literal(PickSessionStrategy.SINGLE_ITEM),
});

const MultiItemPickSessionSchema = PickSessionBaseSchema.extend({
  strategy: z.literal(PickSessionStrategy.MULTI_ITEM),
});

// const BulkShipmentPickSessionSchema = PickSessionBaseSchema.extend({
//   strategy: z.literal(PickSessionStrategy.BULK_SHIPMENT),
// });

export const PickSessionSchema = z.discriminatedUnion("strategy", [
  SingleItemPickSessionSchema,
  MultiItemPickSessionSchema,
  // BulkShipmentPickSessionSchema,
]);

export type PickSession = z.infer<typeof PickSessionSchema>;
export interface IPickSessionSchema extends z.ZodType<PickSession> {}

export function formatPickSessionStrategy(
  strategy: PickSessionStrategy,
): string {
  switch (strategy) {
    case PickSessionStrategy.SINGLE_ITEM:
      return "Single item";
    case PickSessionStrategy.MULTI_ITEM:
      return "Multi item";
    // case PickSessionStrategy.BULK_SHIPMENT:
    //   return "Bulk Shipment Orders";
    default:
      assertNever(strategy);
  }
}

export function ensureValidPickSession({
  pickSession,
}: {
  pickSession: PickSession;
}): { valid: boolean; message?: string } {
  // all same tote for single item
  // 1 unique tote per order multi item
  // tote quant not greater than available quant (total - out of stock)
  // picked quant not greater than total quant
  // out of stock quant not negative
  // picked + out of stock not greater than total quant

  for (const item of pickSession.items) {
    // Validate picked quantity not greater than total quantity
    if (item.quantityPicked > item.totalQuantity) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has picked quantity (${item.quantityPicked}) greater than total quantity (${item.totalQuantity})`,
      };
    }

    // Validate out of stock quantity is not negative
    if (item.quantityOutOfStock < 0) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has negative out of stock quantity (${item.quantityOutOfStock})`,
      };
    }

    // Validate that picked + out of stock doesn't exceed total quantity
    const pickedPlusOutOfStock = item.quantityPicked + item.quantityOutOfStock;
    if (pickedPlusOutOfStock > item.totalQuantity) {
      return {
        valid: false,
        message: `Item ${item._id} group ${item.fulfillmentGroupId} has picked (${item.quantityPicked}) + out of stock (${item.quantityOutOfStock}) = ${pickedPlusOutOfStock} greater than total quantity (${item.totalQuantity})`,
      };
    }

    // Validate tote quantities if they exist
    if (item.quantityByTote) {
      let totalToteQuantity = 0;
      for (const [, tote] of Object.entries(item.quantityByTote)) {
        totalToteQuantity += tote.quantity;
      }

      // Validate tote quantity not greater than picked quantity
      if (totalToteQuantity > item.quantityPicked) {
        return {
          valid: false,
          message: `Item ${item._id} group ${item.fulfillmentGroupId} has tote quantities (${totalToteQuantity}) greater than picked quantity (${item.quantityPicked})`,
        };
      }

      // Validate tote quantity not greater than available quantity (total - out of stock)
      const availableQuantity = item.totalQuantity - item.quantityOutOfStock;
      if (totalToteQuantity > availableQuantity) {
        return {
          valid: false,
          message: `Item ${item._id} group ${item.fulfillmentGroupId} has tote quantities (${totalToteQuantity}) greater than available quantity (${availableQuantity} = total ${item.totalQuantity} - out of stock ${item.quantityOutOfStock})`,
        };
      }
    }

    // For scan_at_cart with multi_item strategy, validate that picked quantities match tote assigned quantities
    // Single item strategy allows skipping tote assignment, so picked items may not have totes assigned
    if (
      pickSession.scanType === ScanType.SCAN_AT_CART &&
      pickSession.strategy === PickSessionStrategy.MULTI_ITEM
    ) {
      const toteQuantity = item.quantityByTote
        ? Object.values(item.quantityByTote).reduce(
            (sum, tote) => sum + tote.quantity,
            0,
          )
        : 0;

      if (item.quantityPicked !== toteQuantity) {
        return {
          valid: false,
          message: `For scan_at_cart, item ${item._id} group ${item.fulfillmentGroupId} has picked quantity (${item.quantityPicked}) that doesn't match tote assigned quantity (${toteQuantity})`,
        };
      }
    }
  }

  // For multi-item strategy, validate that different fulfillmentGroupIds don't share the same tote
  if (pickSession.strategy === PickSessionStrategy.MULTI_ITEM) {
    const toteToFulfillmentGroup = new Map<string, string>();

    for (const item of pickSession.items) {
      if (item.quantityByTote) {
        for (const toteId of Object.keys(item.quantityByTote)) {
          const existingGroup = toteToFulfillmentGroup.get(toteId);
          if (existingGroup && existingGroup !== item.fulfillmentGroupId) {
            return {
              valid: false,
              message: `Multi item pick session cannot have different orders sharing the same tote. Tote ${toteId} is assigned to both fulfillment group ${existingGroup} and ${item.fulfillmentGroupId}`,
            };
          }
          toteToFulfillmentGroup.set(toteId, item.fulfillmentGroupId);
        }
      }
    }
  }

  return { valid: true };
}

export function getToteIdsForFulfillmentGroup(
  pickSession: PickSession,
  fulfillmentGroupId: string,
): string[] {
  const itemsForGroup = pickSession.items.filter(
    (item) => item.fulfillmentGroupId === fulfillmentGroupId,
  );
  const totesForGroup = itemsForGroup.flatMap((item) =>
    Object.keys(item.quantityByTote || {}),
  );
  return totesForGroup;
}

export function getToteIdsExclusiveToFulfillmentGroup(
  pickSession: PickSession,
  fulfillmentGroupId: string,
): string[] {
  const totesForGroup = getToteIdsForFulfillmentGroup(
    pickSession,
    fulfillmentGroupId,
  );
  const itemsNotInThisGroup = pickSession.items.filter(
    (item) => item.fulfillmentGroupId !== fulfillmentGroupId,
  );
  const totesForOtherGroups = itemsNotInThisGroup.flatMap((item) =>
    Object.keys(item.quantityByTote || {}),
  );
  return totesForGroup.filter((tote) => !totesForOtherGroups.includes(tote));
}

export function pickSessionHasAssignedTote(pickSession: PickSession): boolean {
  return pickSession.items.some(
    (item) =>
      item.quantityByTote && Object.keys(item.quantityByTote).length > 0,
  );
}

export function pickSessionHasPickedItems(pickSession: PickSession): boolean {
  return pickSession.items.some((item) => item.quantityPicked > 0);
}

export function allPickSessionItemsResolved(pickSession: PickSession): boolean {
  return pickSession.items.every((item) => {
    return item.quantityPicked + item.quantityOutOfStock >= item.totalQuantity;
  });
}

export function getExistingTotes(pickSession: PickSession): string[] {
  return Array.from(
    new Set(
      pickSession.items.flatMap((item) =>
        Object.keys(item.quantityByTote || {}),
      ),
    ),
  );
}

export function hasExistingTotes(pickSession: PickSession): boolean {
  return getExistingTotes(pickSession).length > 0;
}

export function addTotesToAllItemsInPickSession(
  items: PickSessionItem[],
  toteIds: string[],
): PickSessionItem[] {
  const updatedItems: PickSessionItem[] = items.map((item) => {
    const emptyByTote = Object.fromEntries(
      toteIds.map((toteId) => [toteId, { quantity: 0 }]),
    );

    return {
      ...item,
      quantityByTote: { ...emptyByTote, ...item.quantityByTote },
    };
  });

  return updatedItems;
}
